import React, { FC, useEffect, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { KButton, KDropdown, KSpan } from "kahuna-base-react-components"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"

import { releasePlans } from "../../../apis"
import { errorFilledToastOptions, successFilledToastOptions } from "../../../constants"
import { lang } from "../../../constants/languages"
import { CoManagerReleasePlan, ReleasePlanMetadata as Metadata, ReleasePlanMetadataRole } from "../../../types"
import { hashids, mapToCamelCase } from "../../../utility"
import ReleasePlanArtistPicker, { SpotifyMap } from "./ReleasePlanArtistPicker"

const ROLES: ReleasePlanMetadataRole[] = ["composer", "lyricist", "producer"]

type TrackForm = { id: string; title: string; instrumental: boolean; artist: string[] } & {
  [role in ReleasePlanMetadataRole]: string[]
}

/**
 * The metadata step done on the plan itself: the metadata language and the genre of the release and the
 * main artist, composer, lyricist and producer of every track, the credits the stores show. The names are picked the way
 * the release registry picks them, from Spotify and the account's own artists. The same fields are in the release
 * registry, which the form links to for everything else a track carries.
 *
 * A submitted release is shown read-only: its metadata is changed through an edit request on its page.
 */
const ReleasePlanMetadata: FC<{
  plan: CoManagerReleasePlan
  onSaved: () => void
}> = ({ plan, onSaved }) => {
  const navigate = useNavigate()
  const words = lang.co_manager
  const [metadata, setMetadata] = useState<Metadata | null>(null)
  const [languageId, setLanguageId] = useState<string | undefined>(undefined)
  const [genreId, setGenreId] = useState<string | undefined>(undefined)
  const spotifyMap = useRef<SpotifyMap>({})
  const [tracks, setTracks] = useState<TrackForm[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState("")

  const errorText = (failure: any) => {
    const messages = words.release_plan_errors as { [key: string]: string }
    return messages[failure?.response?.data?.error] || messages.generic
  }

  const load = (loaded: Metadata) => {
    // Only the read carries the language list, a save answers with the metadata alone.
    setMetadata((previous) => ({
      ...loaded,
      metadataLanguages: loaded.metadataLanguages || previous?.metadataLanguages,
      genres: loaded.genres || previous?.genres
    }))
    setLanguageId(loaded.metadataLanguageId || undefined)
    setGenreId(loaded.subgenreId || undefined)
    setTracks(
      loaded.tracks.map((track) => ({
        id: track.id,
        title: track.title,
        instrumental: track.instrumental,
        artist: track.artist || [],
        composer: track.composer,
        lyricist: track.lyricist,
        producer: track.producer
      }))
    )
  }

  useEffect(() => {
    releasePlans(`/${plan.id}/metadata/`, "get")
      .then((response) => load(mapToCamelCase<Metadata>(response.data)))
      .catch((failure) => setError(errorText(failure)))
      .finally(() => setLoading(false))
  }, [plan.id])

  const registryPath = () => {
    if (!plan.releaseId) return "/catalog"
    const hash = hashids.encode(String(plan.releaseId))
    return plan.releaseStatus === "draft" ? `/catalog/asset-registry/${hash}` : `/catalog/${hash}`
  }

  const updateTrack = (id: string, change: Partial<TrackForm>) =>
    setTracks((previous) => previous.map((track) => (track.id === id ? { ...track, ...change } : track)))

  // An album is mostly credited the same on every song, so the first song's credits go onto the rest at once.
  const copyToAll = (source: TrackForm) =>
    setTracks((previous) =>
      previous.map((track) => ({
        ...track,
        artist: source.artist,
        composer: source.composer,
        lyricist: source.lyricist,
        producer: source.producer,
        instrumental: source.instrumental
      }))
    )

  const save = () => {
    setError("")
    setSaving(true)
    releasePlans(`/${plan.id}/metadata/`, "post", {
      metadata_language_id: languageId,
      subgenre_id: genreId,
      artist_name_spotify_map: spotifyMap.current,
      tracks: tracks.map((track) => ({
        id: track.id,
        instrumental: track.instrumental,
        artist: track.artist,
        composer: track.composer,
        lyricist: track.instrumental ? [] : track.lyricist,
        producer: track.producer
      }))
    })
      .then((response) => {
        load(mapToCamelCase<Metadata>(response.data))
        toast.success(words.release_plan_metadata_saved, successFilledToastOptions)
        onSaved()
      })
      .catch((failure) => {
        setError(errorText(failure))
        toast.error(errorText(failure), errorFilledToastOptions)
      })
      .finally(() => setSaving(false))
  }

  const label = (text: string) => <KSpan text={text} fontSize={11} lineHeight="15px" color="#888" />
  const missingWords = words.release_plan_metadata_missing as { [key: string]: string }
  const roleWords = words.release_plan_metadata_roles as { [key: string]: string }

  const wizardLink = (
    <KButton
      dataTestId="co-manager-plan-metadata-wizard"
      text={words.release_plan_metadata_wizard}
      onClick={() => navigate(registryPath())}
      background="transparent"
      shadowDisabled
      width="auto"
      height="auto"
      padding="0px"
      fontSize={12}
      textColor="#6B7280"
    />
  )

  if (loading) {
    return <div className="co-manager-plan-metadata">{label(words.release_plan_metadata_loading)}</div>
  }

  if (!metadata) {
    return (
      <div className="co-manager-plan-metadata">
        {!!error && <KSpan text={error} fontSize={12} lineHeight="16px" color="#E5484D" />}
        {wizardLink}
      </div>
    )
  }

  const languageOptions: KSelectOption[] = (metadata.metadataLanguages || []).map((language) => ({
    label: language.label,
    value: Number(language.value),
    value2: String(language.value)
  }))
  const genreOptions: KSelectOption[] = (metadata.genres || []).map((genre) => ({
    label: genre.label,
    value: Number(genre.value),
    value2: String(genre.value)
  }))
  const missingText = metadata.missing.map((key) => missingWords[key] || key).join(", ")

  return (
    <div className="co-manager-plan-metadata">
      {/* Complete only with a track: without one there are no credits yet, whatever the language. */}
      {metadata.missing.length
        ? label(`${words.release_plan_metadata_still_missing} ${missingText}`)
        : !!metadata.tracks.length && label(words.release_plan_metadata_complete)}

      {!metadata.tracks.length && label(words.release_plan_metadata_no_tracks)}

      {metadata.editable ? (
        <>
          {!!languageOptions.length && label(words.release_plan_metadata_language)}
          {!!languageOptions.length && (
            <KDropdown
              dataTestId="co-manager-plan-metadata-language"
              options={languageOptions}
              selected={languageOptions.find((option) => option.value2 === languageId)}
              onSelect={(selected) => setLanguageId((selected as KSelectOption)?.value2)}
              placeholder={words.release_plan_metadata_language}
            />
          )}
          {!!genreOptions.length && label(words.release_plan_genre)}
          {!!genreOptions.length && (
            <KDropdown
              dataTestId="co-manager-plan-metadata-genre"
              options={genreOptions}
              selected={genreOptions.find((option) => option.value2 === genreId)}
              onSelect={(selected) => setGenreId((selected as KSelectOption)?.value2)}
              placeholder={words.release_plan_genre}
            />
          )}
          {tracks.map((track, index) => (
            <div className="co-manager-plan-row" key={`plan-metadata-${track.id}`}>
              <KSpan text={track.title || words.release_plan_metadata_untitled} fontSize={13} fontWeight={500} />
              {/* The main artist first, as in the release registry's tracks: it is what the stores show the song under. */}
              <div className="co-manager-plan-metadata-field">
                {label(words.release_plan_main_artist)}
                <ReleasePlanArtistPicker
                  value={track.artist}
                  spotifyMap={spotifyMap.current}
                  placeholder={words.release_plan_artist_search}
                  onChange={(names) => updateTrack(track.id, { artist: names })}
                />
              </div>
              {ROLES.map((role) =>
                role === "lyricist" && track.instrumental ? null : (
                  <div className="co-manager-plan-metadata-field" key={`plan-metadata-${track.id}-${role}`}>
                    {label(roleWords[role])}
                    <ReleasePlanArtistPicker
                      value={track[role]}
                      spotifyMap={spotifyMap.current}
                      placeholder={words.release_plan_artist_search}
                      onChange={(names) => updateTrack(track.id, { [role]: names })}
                    />
                  </div>
                )
              )}
              <label className="co-manager-plan-checkbox">
                <input
                  type="checkbox"
                  checked={track.instrumental}
                  disabled={saving}
                  onChange={(event) => updateTrack(track.id, { instrumental: event.target.checked })}
                />
                <KSpan text={words.release_plan_metadata_instrumental} fontSize={12} lineHeight="16px" />
              </label>
              {index === 0 && tracks.length > 1 && (
                <div className="self-start">
                  <KButton
                    dataTestId="co-manager-plan-metadata-copy"
                    text={words.release_plan_metadata_copy_all}
                    onClick={() => copyToAll(track)}
                    disabled={saving}
                    background="transparent"
                    shadowDisabled
                    width="auto"
                    height="auto"
                    padding="0px"
                    fontSize={12}
                    textColor="#6B7280"
                  />
                </div>
              )}
            </div>
          ))}
          {!!error && <KSpan text={error} fontSize={12} lineHeight="16px" color="#E5484D" />}
          <div className="co-manager-plan-metadata-actions">
            <KButton
              dataTestId="co-manager-plan-metadata-save"
              text={saving ? words.release_plan_metadata_saving : words.release_plan_metadata_save}
              onClick={save}
              // The language and the genre are the product's, so they can be saved before there is a track to credit.
              disabled={saving || (!metadata.tracks.length && !languageId && !genreId)}
              background="#111"
              textColor="#FFF"
              width="auto"
              height="30px"
              padding="6px 14px"
              borderRadius={100}
              fontSize={12}
            />
            {wizardLink}
          </div>
        </>
      ) : (
        <>
          {!!metadata.metadataLanguage &&
            label(`${words.release_plan_metadata_language}: ${metadata.metadataLanguage}`)}
          {metadata.tracks.map((track) => (
            <KSpan
              key={`plan-metadata-${track.id}`}
              text={`${track.title}: ${[
                `${words.release_plan_main_artist} ${(track.artist || []).join(", ") || "-"}`,
                ...ROLES.map((role) => `${roleWords[role]} ${track[role].join(", ") || "-"}`)
              ].join(" · ")}`}
              fontSize={12}
              lineHeight="16px"
              color="#666"
            />
          ))}
          {label(words.release_plan_metadata_submitted)}
          {wizardLink}
        </>
      )}
    </div>
  )
}

export default ReleasePlanMetadata
